import type { Database } from './database.types'
import type { CentimeClient } from './supabase'

type PreferencesRow = Database['public']['Tables']['notification_preferences']['Row']

export interface NotificationPreferences {
  pushEnabled: boolean
  emailEnabled: boolean
  /** Heure locale d'envoi, de 6 à 22. */
  sendHour: number
  bills: boolean
  income: boolean
  envelopes: boolean
  review: boolean
  weekly: boolean
  nudges: boolean
  /** Nouveautés et conseils (campagnes e-mail de l'équipe). */
  news: boolean
}

/** Réglages par défaut (identiques aux valeurs par défaut de la base). */
export const DEFAULT_PREFERENCES: NotificationPreferences = {
  pushEnabled: true,
  emailEnabled: true,
  sendHour: 20,
  bills: true,
  income: true,
  envelopes: true,
  review: true,
  weekly: true,
  nudges: true,
  news: true,
}

export function preferencesFromRow(row: PreferencesRow | null): NotificationPreferences {
  if (!row) return { ...DEFAULT_PREFERENCES }
  return {
    pushEnabled: row.push_enabled,
    emailEnabled: row.email_enabled,
    sendHour: row.send_hour,
    bills: row.bills,
    income: row.income,
    envelopes: row.envelopes,
    review: row.review,
    weekly: row.weekly,
    nudges: row.nudges,
    news: row.news,
  }
}

export async function fetchPreferences(client: CentimeClient): Promise<NotificationPreferences> {
  const { data, error } = await client.from('notification_preferences').select('*').maybeSingle()
  if (error) throw error
  return preferencesFromRow(data)
}

export async function savePreferences(
  client: CentimeClient,
  userId: string,
  preferences: NotificationPreferences,
): Promise<NotificationPreferences> {
  const { data, error } = await client
    .from('notification_preferences')
    .upsert({
      user_id: userId,
      push_enabled: preferences.pushEnabled,
      email_enabled: preferences.emailEnabled,
      send_hour: preferences.sendHour,
      bills: preferences.bills,
      income: preferences.income,
      envelopes: preferences.envelopes,
      review: preferences.review,
      weekly: preferences.weekly,
      nudges: preferences.nudges,
      news: preferences.news,
    })
    .select('*')
    .single()
  if (error) throw error
  return preferencesFromRow(data)
}

export async function registerPushSubscription(
  client: CentimeClient,
  subscription: PushSubscriptionJSON,
): Promise<void> {
  const { endpoint, keys } = subscription
  if (!endpoint || !keys?.p256dh || !keys.auth) throw new Error('Abonnement push incomplet')
  const { error } = await client.rpc('register_push_subscription', {
    p_endpoint: endpoint,
    p_p256dh: keys.p256dh,
    p_auth: keys.auth,
    p_user_agent: navigator.userAgent.slice(0, 300),
  })
  if (error) throw error
}

export async function removePushSubscription(
  client: CentimeClient,
  endpoint: string,
): Promise<void> {
  const { error } = await client.rpc('remove_push_subscription', { p_endpoint: endpoint })
  if (error) throw error
}

/** Désinscription des e-mails depuis le lien reçu, sans connexion. */
/** Désinscription par lien : rappels (par défaut) ou nouveautés. */
export async function unsubscribeEmail(
  client: CentimeClient,
  token: string,
  kind: 'reminders' | 'news' = 'reminders',
): Promise<boolean> {
  const { data, error } = await client.rpc('unsubscribe_email', { p_token: token, p_kind: kind })
  if (error) throw error
  return data
}

/** Notification d'essai sur les appareils de l'utilisateur connecté. */
export async function sendTestNotification(client: CentimeClient): Promise<boolean> {
  const { data, error } = await client.functions.invoke<{ delivered?: boolean }>('notify', {
    body: { test: true },
  })
  if (error) return false
  return data?.delivered === true
}
